import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Space, Typography, Alert, Tag, Divider, Input, List } from 'antd';
import { SendOutlined, ReloadOutlined, ClearOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { getDataProcessingWebSocketUrl, WebSocketManager } from '../utils/websocket';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const WebSocketTest = () => {
  const { user } = useAuth();
  const [wsConnected, setWsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [testMessage, setTestMessage] = useState('');
  const wsManagerRef = useRef(null);

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (wsManagerRef.current) {
        wsManagerRef.current.close();
      }
    };
  }, [user]);

  const connectWebSocket = () => {
    // 清理现有连接
    if (wsManagerRef.current) {
      wsManagerRef.current.close();
    }

    // 检查用户信息
    if (!user || !user.id) {
      addMessage('系统', '用户信息不完整，无法建立WebSocket连接', 'warning');
      return;
    }

    // 获取token
    const token = localStorage.getItem('token');
    if (!token) {
      addMessage('系统', '未找到认证token，无法建立WebSocket连接', 'error');
      return;
    }

    // 获取WebSocket URL
    const wsUrl = getDataProcessingWebSocketUrl(user.id, token);
    if (!wsUrl) {
      addMessage('系统', '无法获取WebSocket连接地址', 'error');
      return;
    }

    addMessage('系统', `正在连接: ${wsUrl}`, 'info');

    // 创建WebSocket管理器
    wsManagerRef.current = new WebSocketManager(wsUrl);

    // 设置事件处理器
    wsManagerRef.current.onOpen = () => {
      setWsConnected(true);
      addMessage('系统', 'WebSocket连接已建立', 'success');
    };

    wsManagerRef.current.onMessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        addMessage('服务器', JSON.stringify(message, null, 2), 'info');
      } catch (error) {
        addMessage('系统', `解析消息失败: ${error.message}`, 'error');
      }
    };

    wsManagerRef.current.onClose = () => {
      setWsConnected(false);
      addMessage('系统', 'WebSocket连接已断开', 'warning');
    };

    wsManagerRef.current.onError = (error) => {
      setWsConnected(false);
      addMessage('系统', `WebSocket错误: ${error.message || error}`, 'error');
    };

    wsManagerRef.current.onReconnect = (attempt) => {
      addMessage('系统', `正在尝试重新连接... (${attempt}/5)`, 'info');
    };

    // 开始连接
    wsManagerRef.current.connect();
  };

  const addMessage = (sender, content, type = 'info') => {
    const message = {
      id: Date.now() + Math.random(),
      sender,
      content,
      type,
      timestamp: new Date().toLocaleTimeString()
    };
    setMessages(prev => [...prev, message]);
  };

  const sendMessage = () => {
    if (!wsConnected || !testMessage.trim()) return;
    
    if (wsManagerRef.current && wsManagerRef.current.send(testMessage)) {
      addMessage('客户端', testMessage, 'success');
      setTestMessage('');
    } else {
      addMessage('系统', '发送失败: WebSocket未连接', 'error');
    }
  };

  const sendPing = () => {
    if (!wsConnected) return;
    
    if (wsManagerRef.current && wsManagerRef.current.send('ping')) {
      addMessage('客户端', 'ping', 'info');
    } else {
      addMessage('系统', '发送ping失败: WebSocket未连接', 'error');
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const getMessageColor = (type) => {
    switch (type) {
      case 'success': return '#52c41a';
      case 'error': return '#ff4d4f';
      case 'warning': return '#faad14';
      case 'info': return '#1890ff';
      default: return '#666';
    }
  };

  return (
    <div className="p-6">
      <Card title="WebSocket连接测试" className="mb-6">
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          
          {/* 连接状态 */}
          <Alert
            message={
              <Space>
                <span>连接状态:</span>
                <Tag color={wsConnected ? 'green' : 'red'}>
                  {wsConnected ? '已连接' : '未连接'}
                </Tag>
                <Button 
                  size="small" 
                  onClick={connectWebSocket}
                  icon={<ReloadOutlined />}
                >
                  重新连接
                </Button>
              </Space>
            }
            type={wsConnected ? 'success' : 'warning'}
            showIcon
          />

          {/* 控制按钮 */}
          <Card title="控制面板" size="small">
            <Space>
              <Button 
                type="primary" 
                onClick={sendPing}
                disabled={!wsConnected}
                icon={<SendOutlined />}
              >
                发送Ping
              </Button>
              <Button 
                onClick={clearMessages}
                icon={<ClearOutlined />}
              >
                清空消息
              </Button>
            </Space>
          </Card>

          {/* 消息发送 */}
          <Card title="发送消息" size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <TextArea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="输入要发送的消息..."
                rows={3}
                onPressEnter={(e) => {
                  if (e.ctrlKey) {
                    sendMessage();
                  }
                }}
              />
              <Space>
                <Button 
                  type="primary" 
                  onClick={sendMessage}
                  disabled={!wsConnected || !testMessage.trim()}
                  icon={<SendOutlined />}
                >
                  发送消息
                </Button>
                <Text type="secondary">按 Ctrl+Enter 快速发送</Text>
              </Space>
            </Space>
          </Card>

          {/* 消息列表 */}
          <Card title="消息记录" size="small">
            <div style={{ maxHeight: '400px', overflow: 'auto' }}>
              <List
                dataSource={messages}
                renderItem={(item) => (
                  <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                    <div style={{ width: '100%' }}>
                      <div style={{ marginBottom: '4px' }}>
                        <Text type="secondary">[{item.timestamp}]</Text>
                        <Tag color={getMessageColor(item.type)} style={{ marginLeft: 8 }}>
                          {item.sender}
                        </Tag>
                      </div>
                      <Paragraph 
                        style={{ 
                          margin: 0, 
                          color: getMessageColor(item.type),
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {item.content}
                      </Paragraph>
                    </div>
                  </List.Item>
                )}
              />
            </div>
          </Card>

          {/* 使用说明 */}
          <Card title="使用说明" size="small">
            <Paragraph>
              <Text strong>功能说明：</Text>
            </Paragraph>
            <ul>
              <li>此页面用于测试WebSocket连接和消息推送功能</li>
              <li>连接建立后，可以发送ping消息测试连接</li>
              <li>可以发送自定义消息测试服务器响应</li>
              <li>所有接收到的消息都会显示在消息记录中</li>
              <li>在数据处理页面，服务器会自动推送处理进度</li>
            </ul>
            <Paragraph>
              <Text strong>测试步骤：</Text>
            </Paragraph>
            <ol>
              <li>确保后端服务正在运行（端口8000）</li>
              <li>点击"重新连接"建立WebSocket连接</li>
              <li>发送ping消息测试连接</li>
              <li>切换到数据处理页面测试实时进度推送</li>
            </ol>
          </Card>
        </Space>
      </Card>
    </div>
  );
};

export default WebSocketTest; 